// Lightweight, optional client-side aggregates for chat responses.
//
// The chat's main job is to surface rows in the existing dashboard
// tables, but it's also useful to echo a one-line numeric summary so
// the user knows the scope at a glance:
//   "Found 42 positions. Total volume (Buy - Sell): +320,000."
//
// Aggregation specs are intentionally simple - sum/count/group-by over
// named numeric fields. Anything more interactive should be done in the
// embedded table.

export interface AggregateSpec {
    countLabel?: string;          // e.g. "positions" - drives the leading count phrase
    sumFields?: string[];         // dot-paths into each row to sum (e.g. "volume")
    netSignField?: string;        // optional field whose value 'Buy'/'Sell' flips sumFields sign for a net total
}

export interface AggregateResult {
    count: number;
    sums: Record<string, number>;
    nets: Record<string, number>;
}

function readPath(row: any, path: string): unknown {
    if (!row || !path) return undefined;
    const parts = path.split('.');
    let current: any = row;
    for (const part of parts) {
        if (current == null) return undefined;
        current = current[part];
    }
    return current;
}

function toNumber(value: unknown): number {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string') {
        const n = Number.parseFloat(value);
        return Number.isFinite(n) ? n : 0;
    }
    return 0;
}

export function aggregateRows(rows: unknown[], spec: AggregateSpec): AggregateResult {
    const sums: Record<string, number> = {};
    const nets: Record<string, number> = {};
    for (const field of spec.sumFields ?? []) {
        sums[field] = 0;
        nets[field] = 0;
    }
    for (const row of rows) {
        const sign = spec.netSignField
            ? (String(readPath(row, spec.netSignField) ?? '').toLowerCase() === 'sell' ? -1 : 1)
            : 1;
        for (const field of spec.sumFields ?? []) {
            const v = toNumber(readPath(row, field));
            sums[field] += v;
            nets[field] += v * sign;
        }
    }
    return { count: rows.length, sums, nets };
}

// Format a number with thousands separators and at most 2 decimals.
function formatNumber(n: number): string {
    if (!Number.isFinite(n)) return '0';
    const rounded = Math.round(n * 100) / 100;
    return rounded.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

export function describeAggregate(result: AggregateResult, spec: AggregateSpec): string {
    const parts: string[] = [];
    parts.push(`Found **${formatNumber(result.count)}** ${spec.countLabel ?? 'rows'}.`);
    // When a netSignField is configured (e.g. positions), the "total" we report
    // is the net (Buy - Sell) value rather than the gross sum, since the gross
    // sum is misleading when buys and sells are mixed.
    const useNet = !!spec.netSignField;
    for (const field of spec.sumFields ?? []) {
        const value = useNet ? (result.nets[field] ?? 0) : (result.sums[field] ?? 0);
        if (value !== 0) {
            const label = useNet ? `Total ${field} (Buy - Sell)` : `Total ${field}`;
            const sign = useNet && value > 0 ? '+' : '';
            parts.push(`${label}: **${sign}${formatNumber(value)}**.`);
        }
    }
    return parts.join(' ');
}
